"use client";

import { useEffect } from "react";

type NewRelicAttributes = Record<string, string | number | boolean>;

declare global {
  interface Window {
    NRBA_LOADED?: boolean;
    NREUM?: Record<string, unknown>;
  }
}

interface NewRelicProps {
  lob?: string;
  attributes?: NewRelicAttributes;
}

export function NewRelic({ lob, attributes }: NewRelicProps) {
  useEffect(() => {
    initNewRelic(lob, attributes);
  }, [lob, attributes]);

  return null;
}

function initNewRelic(lob?: string, attributes?: NewRelicAttributes) {
  if (typeof window === "undefined" || window.NRBA_LOADED) return;

  const accountID = process.env.NR_ACCOUNT_ID;
  const applicationID = lob === "hen" ? process.env.NR_APPLICATION_ID_HEN : process.env.NR_APPLICATION_ID;
  const licenseKey = process.env.NR_LICENSE_KEY;
  const trustKey = process.env.NR_TRUST_KEY;

  if (!accountID || !applicationID || !licenseKey) return;

  if (!window.NREUM || typeof window.NREUM !== "object") {
    window.NREUM = {};
  }

  const loadAgent = async () => {
    try {
      const { BrowserAgent } = await import("@newrelic/browser-agent/loaders/browser-agent");

      new BrowserAgent({
        info: {
          applicationID,
          beacon: "bam.nr-data.net",
          errorBeacon: "bam.nr-data.net",
          licenseKey,
          sa: 1,
          jsAttributes: attributes ?? {},
        },
        loader_config: {
          accountID,
          trustKey,
          agentID: applicationID,
          applicationID,
          licenseKey,
        },
        init: {
          session_replay: {
            enabled: true,
            block_selector: "",
            mask_text_selector: "",
            sampling_rate: 100.0,
            error_sampling_rate: 100.0,
            mask_all_inputs: true,
            collect_fonts: true,
            inline_images: false,
            fix_stylesheets: true,
            preload: false,
          },
          distributed_tracing: { enabled: true },
          privacy: { cookies_enabled: true },
          ajax: { deny_list: ["bam.nr-data.net"] },
        },
      });

      window.NRBA_LOADED = true;
    } catch (error) {
      console.error("New Relic initialization failed", error);
    }
  };

  void loadAgent();
}
